import { updateDefaultLights } from 'entangled-worlds-orbiters-shared/core';
import { MoonsManager } from 'entangled-worlds-orbiters-shared/moons';
import { isPerformanceModeEnabled } from '../../config/performanceMode.js';
// Which files a world draws with is one question with one answer — an offline download asks it too,
// before anything is drawn, and a second copy of these candidate lists would let the two diverge.
import {
  extractPreview,
  resolveCloudsTextureUrl,
  resolveMoonsTextureUrl,
  resolveTextureUrl,
} from '../../world/worldTextureUrls.js';

function toHexInt(hex) {
  if (typeof hex === 'number') return hex >>> 0;
  if (typeof hex !== 'string') return undefined;
  const clean = hex.trim().replace(/^#/, '');
  if (!clean) return undefined;
  const parsed = Number.parseInt(clean, 16);
  return Number.isFinite(parsed) ? (parsed >>> 0) : undefined;
}

function applyLighting(scene, metadata = {}, preview = {}) {
  const lighting = preview.lighting || metadata.lighting || {};
  const ambient = lighting.ambientIntensity ?? lighting.ambient ?? 1;
  const diffuse = lighting.diffuseIntensity ?? lighting.diffuse ?? 1;
  const color = lighting.lightColor ?? lighting.color ?? '#ffffff';
  const direction = lighting.lightDirection ?? lighting.direction ?? { x: 1, y: 1, z: 1 };

  const safeDirection =
    direction && typeof direction === 'object'
      ? {
          x: Number.isFinite(direction.x) ? direction.x : 1,
          y: Number.isFinite(direction.y) ? direction.y : 1,
          z: Number.isFinite(direction.z) ? direction.z : 1,
        }
      : { x: 1, y: 1, z: 1 };

  // The lights belong to THIS voice's scene. Addressing them without it updated whichever scene was
  // ensured last, so in a multi-voice realm one voice's lighting landed on another's world.
  if (!scene) {
    console.warn('[OrbitersPlayMode] No scene — skipping lighting.');
    return;
  }
  updateDefaultLights(scene, {
    ambientIntensity: ambient,
    diffuseIntensity: diffuse,
    lightColor: color,
    lightDirection: safeDirection,
  });
}

function applyFlags(worldManager, metadata = {}, preview = {}) {
  const flags = {
    ...(metadata.flags || {}),
    ...(preview.flags || {}),
  };
  if (worldManager && typeof worldManager.setFlags === 'function' && Object.keys(flags).length) {
    worldManager.setFlags(flags);
  }
}

async function setupMoons({
  world, preview, scene, moonsManager, renderer,
}) {
  if (!scene) return { moonsManager };

  let manager = moonsManager;
  if (!manager) {
    // Thread this voice's renderer so KTX2 moon textures bind to its context (shared 3.0: no
    // window-global fallback exists).
    manager = new MoonsManager(scene, { renderer });
  }

  const metadata = world?.metadata || {};
  const previewMoons = preview.moons || metadata.moons || {};

  let moonCount = 0;
  if (typeof previewMoons.count === 'number') {
    moonCount = previewMoons.count;
  } else if (typeof world?.moonAmount === 'number') {
    moonCount = world.moonAmount;
  } else if (typeof metadata.moonAmount === 'number') {
    moonCount = metadata.moonAmount;
  }

  const resolvedTexture = resolveMoonsTextureUrl(world, preview);
  const moonColor =
    previewMoons.moonColor ||
    previewMoons.color ||
    metadata?.moons?.moonColor ||
    '#ffffff';

  const seed =
    world?.worldId ||
    metadata.worldId ||
    metadata.id ||
    'orbiters-world';

  const orbitRadius =
    previewMoons.orbitRadius ??
    metadata?.moons?.orbitRadius ??
    1.5;

  const options = {
    seed,
    orbitRadius,
    moonColor,
    useTexture: !!resolvedTexture,
  };
  if (resolvedTexture) {
    options.textureKey = resolvedTexture;
  }

  try {
    await manager.setAmount(moonCount, options);
  } catch (err) {
    console.warn('[OrbitersPlayMode] Failed to configure moons:', err);
  }

  return { moonsManager: manager };
}

export class OrbitersPlayMode {
  /**
   * @param {{ is3dOff?: () => boolean }} deps `is3dOff` is asked once per activation, at the moment
   *   the world would be built. It is a dependency rather than a module-level read because the scene
   *   profile has ONE owner — the scene profile controller — and this class only builds what that
   *   owner has already decided. The default keeps a play mode constructed without it (edit mode
   *   builds its own loader) reading the same preference as everything else.
   */
  /** Bumped by every `activate` — see the note there. Only the newest activation may touch the scene. */
  #activation = 0;

  constructor({ worldManager, scene, onWorldHydrated, is3dOff } = {}) {
    this.worldManager = worldManager;
    this.scene = scene;
    this.onWorldHydrated = onWorldHydrated;
    this.is3dOff = typeof is3dOff === 'function' ? is3dOff : isPerformanceModeEnabled;
    this.isActive = false;
    this.lastContext = null;
    this.moonsManager = null;
  }

  async refresh(context) {
    return this.activate(context);
  }

  async activate(context = {}) {
    this.isActive = true;
    this.lastContext = context;
    // Which activation this is. `activate` awaits the network (textures, moons) and card swaps arrive
    // faster than those resolve, so two can be in flight at once — and the LOSER must not act on the
    // scene it comes back to. `WorldManager.setMode` already carries its own token for the world it
    // builds; this covers the teardown below, which reaches for `disposeCurrent()` directly and would
    // otherwise let a superseded activation's late failure dispose the world the NEWER one just
    // installed, leaving the current card with no world at all.
    const activation = ++this.#activation;
    const supersededActivation = () => activation !== this.#activation;

    const world = context.trackData?.entangledWorld || context.entangledWorld;
    if (!world) {
      console.warn('[OrbitersPlayMode] Missing entangled world data – skipping shared renderer.');
      return false;
    }

    // With the 3D off there is nothing to build, and building it anyway is not a harmless waste — it
    // is the cost that shows up as the audio breaking. The planet's texture is fetched and decoded,
    // the moons' compressed textures are transcoded, geometry is uploaded; all of it on the main
    // thread, and all of it for a scene no renderer will ever draw. This is also what made adding a
    // voice cut the sound for about a second, because a new voice does exactly this work.
    //
    // `lastContext` is still recorded above, so turning the 3D back on rebuilds from the same world
    // data that was in force here, without going back to the network for it.
    if (this.is3dOff()) {
      return true;
    }

    // The preference above says whether the 3D is WANTED. This says whether it is POSSIBLE, and they
    // are not the same question — which is what made a card in a room die rather than load.
    //
    // The renderer belongs to `WorldSceneController` and the preference to the scene profile
    // controller, and the two can legitimately disagree for a moment: a realm voice asked for the 3D
    // back before its compositor exists is left with no renderer (`#build3d` returns early rather
    // than building a second context over the realm's), while the preference already reads "on".
    // Every world builder below needs the real renderer — the texture loaders THROW without it — so
    // building here threw out of the world, out of `setMode`, and out of session bootstrap, taking
    // the whole card with it: audio loaded, nothing playable, and in a room the deck barrier released
    // on a load that had failed.
    //
    // A missing renderer is not an error, it is "there is nothing to draw with yet". Treated as such
    // it degrades exactly like the no-3D mode — the card loads, plays, and acks — and the world is
    // rebuilt from `lastContext` when the renderer arrives, the same way turning the 3D back on does.
    if (!this.worldManager?.renderer) {
      console.warn(
        '[OrbitersPlayMode] No renderer for this voice yet — loading the card without its world.',
        { hasWorldManager: Boolean(this.worldManager), is3dOff: this.is3dOff() },
      );
      return true;
    }

    const metadata = world.metadata || {};
    const preview = extractPreview(metadata);

    applyLighting(this.scene || context.scene, metadata, preview);
    applyFlags(this.worldManager, metadata, preview);

    const textureUrl = resolveTextureUrl(world, preview);
    const graphicsPreference = context.graphicsPreference || 'high';

    const sphere = preview.sphere || metadata.sphere || {};
    const glow = preview.glow || metadata.glow || {};
    const clouds = preview.clouds || metadata.clouds || {};
    const cloudsTextureUrl = resolveCloudsTextureUrl(world, preview);

    const surfaceOptions = {
      sphereRadius: sphere.radius,
      sphereWidthSegments: sphere.widthSegments,
      sphereHeightSegments: sphere.heightSegments,
      materialRoughness: sphere.materialRoughness ?? sphere.roughness,
      materialMetalness: sphere.materialMetalness ?? sphere.metalness,
      glow: glow.enabled ?? true,
      glowScale: glow.scale,
      glowRimHex: toHexInt(glow.rim ?? glow.rimColor),
      glowFacingHex: toHexInt(glow.facing ?? glow.inner),
      glowBias: glow.bias,
      glowFresnelScale: glow.fresnelScale,
      glowPower: glow.power,
      cloudsEnabled: clouds.enabled ?? false,
      cloudsTextureUrl,
      cloudsColor: toHexInt(clouds.color),
      cloudsOpacity: clouds.opacity,
      cloudsSpeed: clouds.speed,
    };

    const runMoonsSetup = async () => {
      // Checked on the way in AND on the way out. `setupMoons` transcodes textures over the network,
      // so a card swap lands inside it routinely — and `this.moonsManager` is the VOICE's, not this
      // activation's. A superseded activation writing it would hang the previous card's moons on the
      // world the newer one just built.
      if (supersededActivation()) return;
      const result = await setupMoons({
        world,
        preview,
        renderer: this.worldManager?.renderer,
        scene: this.scene || context.scene,
        moonsManager: this.moonsManager,
      });
      if (supersededActivation()) return;
      this.moonsManager = result.moonsManager;
    };

    const runNormalMode = async () => {
      await this.worldManager.setMode(3, surfaceOptions);
      await runMoonsSetup();
      return 3;
    };

    const isRecoverableTextureError = (err) => {
      const message = `${err?.message || err || ''}`.toLowerCase();
      const isDomLoadError =
        typeof Event !== 'undefined'
        && err instanceof Event
        && err.type === 'error';
      const isFetchFailure =
        (err instanceof TypeError && message === 'failed to fetch')
        || (err?.name === 'NetworkError');
      return (
        isDomLoadError ||
        isFetchFailure ||
        message.includes('missing ktx 2.0 identifier') ||
        message.includes('ktx2') ||
        message.includes('basisu') ||
        message.includes('texture') ||
        message.includes('image bitmap')
      );
    };

    // What the plain sphere is allowed to fail on. Wider than `isRecoverableTextureError` because by
    // this point the suspect is usually the RENDERER rather than any one texture — a voice without one
    // fails in the loaders and in three.js itself, and those read as WebGL/context errors.
    //
    // PHRASES, not bare words. Matching "renderer" or "context" anywhere in a message also catches
    // ordinary programming errors — `Cannot read properties of undefined (reading 'renderer')` from a
    // bug in the surface options, or anything mentioning `AudioContext` on its way out of the moons —
    // and turning one of those into a world-less card that acks READY to the room is precisely the
    // outcome this branch is scoped to avoid.
    const isDrawingError = (err) => {
      if (isRecoverableTextureError(err)) return true;
      const message = `${err?.message || err || ''}`.toLowerCase();
      return (
        message.includes('requires the owning renderer') ||
        message.includes('webglrenderer') ||
        message.includes('webgl context') ||
        message.includes('context lost') ||
        message.includes('createcontext')
      );
    };

    // The LAST fallback, and the only one allowed to give up. A drawing that cannot be built is not a
    // reason to lose the card: the audio, the session and the controls are all already there, and the
    // player would rather have a plain orbiter than an error. Letting this throw is what turned a
    // texture problem into a dead card and, in a room, into a deck barrier released on a load that
    // never completed — see the renderer note at the top of `activate`.
    //
    // Scoped, not blanket: a failure that is NOT about drawing still propagates, because hiding one
    // behind a card that looks loaded is how a broken session gets acked to the rest of the room as
    // ready. This catches "even the simple sphere would not build", not "anything went wrong".
    const runNormalModeOrNothing = async () => {
      try {
        return await runNormalMode();
      } catch (err) {
        if (!isDrawingError(err)) throw err;
        console.error(
          '[OrbitersPlayMode] Plain sphere fallback failed too — loading the card without its world:',
          err,
        );
        return null;
      }
    };

    let activeMode;

    if (!textureUrl) {
      console.warn('[OrbitersPlayMode] No texture URL in world data — rendering the plain sphere.');
      activeMode = await runNormalModeOrNothing();
    } else {
      try {
        await this.worldManager.setMode(1, {
          textureUrl,
          ...surfaceOptions,
        });
        await runMoonsSetup();
        activeMode = 1;
      } catch (err) {
        if (isRecoverableTextureError(err)) {
          console.warn('[OrbitersPlayMode] Texture mode failed, using normal sphere fallback:', err);
          activeMode = await runNormalModeOrNothing();
        } else {
          throw err;
        }
      }
    }

    if (activeMode === null) {
      // ...unless a newer activation has taken over meanwhile, in which case the world now standing
      // is ITS world, and this one has no business disposing it.
      if (supersededActivation()) return true;
      // Take the OLD world down before giving up. `WorldManager.setMode` only disposes the outgoing
      // world once a new one has been built, and a card swap re-enters play mode through `refresh()`
      // — which never calls `deactivate()`. So without this the player is left looking at the
      // PREVIOUS card's planet and moons while hearing the new session, with nothing on screen
      // saying anything is wrong. An empty centre is the honest picture of what happened.
      await this.#shedWorld(context);
      // No world was built, so there is no render state to report and nothing to hydrate from. Said
      // once, plainly, because "the card is up but the planet is not" is otherwise indistinguishable
      // from the no-3D mode when you are reading a log after the fact.
      console.warn('[OrbitersPlayMode] Card loaded without a world.', { textureUrl });
      return true;
    }

    // The world is built; everything past here PUBLISHES it — a window-global other code reads back,
    // and the hydration callback the rest of the voice reacts to. A superseded activation announcing
    // its card here would describe a world that is not on screen, so it stops instead. (What it did
    // build is disposed by the newer activation's own `setMode`, which never adopted it.)
    if (supersededActivation()) return true;

    if (typeof window !== 'undefined') {
      window.__orbitersWorldRenderState = {
        graphicsPreference,
        activeMode,
        textureUrl,
        glowEnabled: surfaceOptions.glow !== false,
        cloudsEnabled: Boolean(surfaceOptions.cloudsEnabled),
      };
    }

    this.onWorldHydrated?.({ ...context, mode: activeMode, textureUrl });
    return true;
  }

  /**
   * Take this voice's world out of the scene — the planet and the moons both. Shared by `deactivate`
   * and by the give-up branch of `activate`, which needs exactly the same teardown for a different
   * reason: there, no new world could be built, and the outgoing one would otherwise stay up.
   */
  async #shedWorld(context = null) {
    this.worldManager?.disposeCurrent?.();

    const fallbackContext = context || this.lastContext || {};
    const scene = this.scene || fallbackContext.scene;
    if (scene && this.moonsManager?.group) {
      scene.remove(this.moonsManager.group);
    }
    if (this.moonsManager) {
      await this.moonsManager.setAmount(0);
    }
    // Dropped, not kept for next time. A moons manager is bound to the renderer it was built with —
    // that is how its compressed textures get transcoded — and the renderer does not survive the 3D
    // being turned off. Reusing this one after a rebuild would bind the new world's moons to a
    // context that no longer exists.
    this.moonsManager = null;
  }

  async deactivate(context = null) {
    if (!this.isActive) return;
    this.isActive = false;
    // Leaving play mode invalidates any activation still in flight, for the same reason a newer
    // activation does: an in-flight one resolving after this would build moons into a scene that has
    // moved on — onto edit mode's world, which the author is in the middle of using.
    this.#activation += 1;
    await this.#shedWorld(context);
  }

  setActiveDimension() {
    return false;
  }
}

export default OrbitersPlayMode;
